import { useGetOrderInspection } from "@/services/Inspections";
import { InspectionDto, Order, OrderStatus } from "@/types";
import OrderStatusEnum from "@/constants/enums/OrderStatusEnum";

type AwaitingStatus = Extract<OrderStatus, "AWAITING_INSPECTION" | "DEVIATION_PENDING">;

export type OrderInspectionView =
  | { kind: "awaiting"; status: AwaitingStatus }
  | { kind: "inspected"; inspection: InspectionDto; prefillWeightKg: number | null }
  | { kind: "none" };

/**
 * Whether an order went through receiving inspection, decided from the order itself so the buyer and the seller
 * get the same answer (the seller can't read the buyer's setting). Inspection replaces the final amount flow.
 */
export const useOrderInspectionView = (order: Order): { isLoading: boolean; view: OrderInspectionView } => {
  const { status } = order;
  // Only contract orders are paired, and the endpoint fails for an order without a pair. An order held for deviation
  // confirmation has been inspected already, so it is read too.
  const enabled = status !== OrderStatusEnum.AWAITING_INSPECTION && !!order.contractId;

  const { data, isLoading } = useGetOrderInspection(order.id, { enabled, retry: false });

  if (status === OrderStatusEnum.AWAITING_INSPECTION) return { isLoading: false, view: { kind: "awaiting", status } };

  // Newest first; more than one only when a stream had to be inspected again by hand.
  const inspection = data?.inspections[0];
  if (enabled && data && inspection) {
    return { isLoading: false, view: { kind: "inspected", inspection, prefillWeightKg: data.prefillWeightKg } };
  }
  // The status alone says it was inspected, so the waiting text shows while it loads or when it can't be read.
  if (status === OrderStatusEnum.DEVIATION_PENDING) return { isLoading: false, view: { kind: "awaiting", status } };
  if (!enabled) return { isLoading: false, view: { kind: "none" } };
  // An error counts as no inspection, so the drawer falls back to the final amount as before.
  return { isLoading, view: { kind: "none" } };
};
